import { Crisp } from 'crisp-sdk-web';

/**
 * CrispService - A simple service to manage Crisp chat integration
 * This service handles initialization, user configuration, and chat visibility
 */
export class CrispService {
  private static instance: CrispService | null = null;
  private isInitialized = false;
  private crispSessionLoaded: Promise<void> | null = null;

  /**
   * Get singleton instance of CrispService
   */
  public static getInstance(): CrispService {
    if (!CrispService.instance) {
      CrispService.instance = new CrispService();
    }
    return CrispService.instance;
  }

  /**
   * Initialize Crisp chat with website ID from environment
   * @param websiteId - Crisp website ID
   * @param tokenId - Optional token ID for session continuity
   */
  public initialize(websiteId: string, tokenId?: string): void {
    if (this.isInitialized) {
      return;
    }

    if (!websiteId) {
      console.error('[CrispService] Cannot initialize: missing website ID');
      return;
    }

    try {
      Crisp.configure(websiteId);
      Crisp.chat.hide();

      if (tokenId) {
        Crisp.setTokenId(tokenId);
      }

      this.setupCrispSessionLoadedEvent();
      this.isInitialized = true;
    } catch (error) {
      console.error('[CrispService] Initialization failed:', error);
    }
  }

  /**
   * Set user information for Crisp chat
   * @param name - User's full name
   * @param email - User's email address
   */
  public setUser(name: string, email: string): void {
    if (!this.isInitialized) {
      console.warn('[CrispService] Cannot set user: service not initialized');
      return;
    }

    try {
      Crisp.user.setEmail(email);
      Crisp.user.setNickname(name);
    } catch (error) {
      console.error('[CrispService] Failed to set user:', error);
    }
  }

  /**
   * Open Crisp chat
   * Waits for session to load before opening
   */
  public async openChat(): Promise<void> {
    if (!this.isInitialized) {
      console.error('[CrispService] Cannot open chat: service not initialized');
      return;
    }

    try {
      // Wait for Crisp session to load
      if (this.crispSessionLoaded) {
        await this.crispSessionLoaded;
      }

      Crisp.chat.show();
      Crisp.chat.open();
    } catch (error) {
      console.error('[CrispService] Failed to open chat:', error);
    }
  }

  /**
   * Setup Crisp session loaded event
   * Creates a promise that resolves when Crisp session is fully loaded
   */
  private setupCrispSessionLoadedEvent(): void {
    this.crispSessionLoaded = new Promise<void>((resolve) => {
      Crisp.session.onLoaded(() => {        
        // Push CreatelyAI segment to categorize users
        try {
          // Use the global $crisp API to push segments
          if (window.$crisp) {
            window.$crisp.push(['set', 'session:segments', [['createlyai']]]);
          }
        } catch (error) {
          console.error('[CrispService] Failed to push segment:', error);
        }
        
        resolve();
        this.setupCrispChatClosedEvent();
      });
    });
  }

  /**
   * Setup event handler for when chat is closed
   * Hides the chat widget when user closes it
   */
  private setupCrispChatClosedEvent(): void {
    Crisp.chat.onChatClosed(() => {
      Crisp.chat.hide();
    });
  }

  /**
   * Check if service is initialized
   */
  public getIsInitialized(): boolean {
    return this.isInitialized;
  }
}
